import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api';

interface Project {
  id: string;
  code: string;
  name_en: string;
  name_ar: string | null;
  primary_city: string | null;
  status: string;
  vehicle_count?: number;
  driver_count?: number;
}

interface ProjectFilterProps {
  value: string | null;
  onChange: (projectId: string | null) => void;
  className?: string;
  storageKey?: string;
}

/**
 * Compact dropdown filter used across the app (PR MAY10 P4B.1).
 * "All projects" sentinel = null. Persists last selection to localStorage
 * if storageKey is provided.
 */
export function ProjectFilter({ value, onChange, className, storageKey }: ProjectFilterProps) {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    let cancelled = false;
    apiClient.get<{ items: Project[] }>('/api/v1/projects')
      .then((r) => { if (!cancelled) setProjects(r.data.items); })
      .catch(() => { /* silently empty list — filter still renders */ });
    return () => { cancelled = true; };
  }, []);

  // Restore persisted selection on first mount
  useEffect(() => {
    if (!storageKey || value !== null) return;
    const saved = localStorage.getItem(storageKey);
    if (saved && saved !== '') onChange(saved);
  }, [storageKey, value, onChange]);

  function handleChange(v: string) {
    const nextId = v === '__all__' ? null : v;
    onChange(nextId);
    if (storageKey) {
      if (nextId) localStorage.setItem(storageKey, nextId);
      else localStorage.removeItem(storageKey);
    }
  }

  return (
    <select
      value={value ?? '__all__'}
      onChange={(e) => handleChange(e.target.value)}
      className={`h-9 rounded-md border border-input bg-background px-3 text-sm ${className ?? ''}`}
      aria-label={t('project.filter', { defaultValue: 'Filter by project' })}
    >
      <option value="__all__">{t('project.allProjects', { defaultValue: 'All projects' })}</option>
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {p.code} — {p.name_en}
        </option>
      ))}
    </select>
  );
}
